import { OctLabel, OctNumberField } from '@oceantech/oceantech-ui';
import { getIn, useFormikContext } from 'formik';
import React, { useRef } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import InputAutoComplete from '../../../../../component/input-autocomplete';
import TextField from '../../../../../component/TextField';
import SelectThuocButton from '../../../../components/select-thuoc-button';
import {
	HDSD_THUOC_OPTIONS,
	LIEU_DUNG_THUOC_OPTIONS,
	THOI_DIEM_DUNG_THUOC,
} from '../../../../constants/Thuoc';
import { IThoiDiemDung } from '../../../../models/ThuocModels';
import { IThongTinThuoc } from '../../model';

type Props = {
	isFunctionalFoods?: boolean;
	onClear: () => void;
	onDrugSelect: (drug: any) => void;
};

const DrugInfo = ({ isFunctionalFoods, onClear, onDrugSelect }: Props) => {
	const { values, setFieldValue, errors, touched } = useFormikContext<any>();
	const dayTotalFieldRef = useRef<HTMLInputElement>(null);

	const formValues = values.drugInfo;
	const formErrors = errors.drugInfo;
	const formTouched = touched.drugInfo;
	const warehouseId = values?.orderInfo?.warehouse?.id;

	const calculateDrugTotal = (formValues: IThongTinThuoc) => {
		const totalFieldName = 'drugInfo.totalQuantity';

		if (!formValues?.numberOfDays || isEmptyQuantity(formValues)) {
			setFieldValue(totalFieldName, '');
			return;
		}

		const dailyDosage = getTotalDaily(formValues);
		const totalQuantity = formValues.numberOfDays * dailyDosage;
		setFieldValue(totalFieldName, totalQuantity);
	};

	const getTotalDaily = (values: IThongTinThuoc) => {
		return (
			(Number(values.quantityInMorning) || 0) +
			(Number(values.quantityInNoon) || 0) +
			(Number(values.quantityInAfternoon) || 0) +
			(Number(values.quantityInEvening) || 0)
		);
	};

	const isEmptyQuantity = (values: any) => {
		return [
			values.quantityInMorning,
			values.quantityInNoon,
			values.quantityInAfternoon,
			values.quantityInEvening,
		].every((item) => !Boolean(item));
	};

	const onFieldChange = (name: string, value: any) => {
		setFieldValue(`drugInfo.${name}`, value);
	};

	return (
		<div className="bg-white radius-2 spaces py-10 px-10 mt-6">
			<Row className="spaces mt-4">
				<Col xs={9} className="position-relative">
					<SelectThuocButton
						label="Tên thuốc"
						labelClassName="min-w-120-px"
						name="drugInfo.medicalItemName"
						setThuocSelected={(selectedDrug) => {
							setFieldValue('drugInfo.medicalItemName', selectedDrug?.name);
							onDrugSelect(selectedDrug);
							if (dayTotalFieldRef.current) {
								dayTotalFieldRef.current.focus();
							}
						}}
						maKhoThuoc={warehouseId?.toString()}
						inputValue={formValues?.medicalItemName ?? formValues?.name ?? ''}
						isFunctionalFoods={isFunctionalFoods}
					/>
				</Col>
				<Col>
					<OctNumberField
						inputClassName="w-100"
						label="SL khả dụng"
						name="drugInfo.iventory"
						labelClassName="ms-0 spaces min-w-95"
						value={formValues?.iventory}
						disabled
					/>
				</Col>
				<Col>
					<TextField
						className="spaces w-100"
						inputClassName="w-100"
						label="Đơn vị"
						name="drugInfo.unit"
						labelClassName="ms-0 min-w-56 spaces"
						value={formValues?.unit ?? ''}
						disabled
					/>
				</Col>
			</Row>

			<Row className="spaces mt-4">
				<Col xs={9} className="d-flex">
					<div className="w-20 d-flex align-items-center">
						<OctLabel label="Số ngày" className="spaces min-w-120" />
						<OctNumberField
							name="drugInfo.numberOfDays"
							value={formValues?.numberOfDays}
							onChange={(value: number) => {
								onFieldChange('numberOfDays', value);
							}}
							onBlur={() => {
								calculateDrugTotal(formValues);
							}}
							inputRef={dayTotalFieldRef}
						/>
					</div>
					<div className="w-20 ps-5 d-flex align-items-center">
						<OctLabel label="Số lượng" className="spaces min-w-80" isRequired />
						<OctNumberField
							name="drugInfo.totalQuantity"
							value={formValues?.totalQuantity}
							onChange={(value: number) => {
								onFieldChange('totalQuantity', value);
							}}
							error={getIn(formErrors, 'totalQuantity')}
							touched={getIn(formTouched, 'totalQuantity')}
						/>
					</div>
					<InputAutoComplete
						label="Liều dùng"
						name="drugInfo.dosageForm"
						options={LIEU_DUNG_THUOC_OPTIONS}
						placeholder="Liều dùng/lần , Số lần/ngày"
						className="spaces w-100"
						inputClassName="w-100"
						labelClassName="min-w-85 ms-8"
						value={formValues?.dosageForm ?? ''}
						required
					/>
				</Col>
				<Col xs={3}>
					<TextField
						inputClassName="w-100"
						label="Đường dùng"
						name="drugInfo.route"
						labelClassName="ms-0 min-w-95px"
						value={formValues?.route ?? ''}
						disabled
					/>
				</Col>
			</Row>
			<Row className="spaces mt-4 d-flex flex-nowrap">
				<Col xs={5}>
					<InputAutoComplete
						className="spaces w-100"
						inputClassName="w-100"
						label="Hướng dẫn SD"
						name="drugInfo.usageInstructions"
						placeholder="Khoảng cách dùng, thời điểm dùng, lưu ý"
						labelClassName="spaces ms-0 min-w-120"
						value={formValues?.usageInstructions ?? ''}
						options={HDSD_THUOC_OPTIONS}
					/>
				</Col>
				<Col xs={4} className="spaces d-flex">
					{THOI_DIEM_DUNG_THUOC.map((option: IThoiDiemDung) => (
						<div className="spaces ps-5 d-flex" key={option.name}>
							<OctLabel label={option.label} className="spaces min-w-55px" />
							<OctNumberField
								name={`drugInfo.${option.name}`}
								value={formValues?.[option.name] ?? ''}
								onChange={(value: number) => {
									onFieldChange(option.name, value);
								}}
								onBlur={() => {
									calculateDrugTotal(formValues);
								}}
							/>
						</div>
					))}
				</Col>
				<Col xs={3} className="d-flex justify-content-end">
					<div className="d-flex gap-4">
						<Button className="btn-fill" type="submit">
							<i className="bi bi-check-lg"></i> Xác nhận
						</Button>
						<Button className="btn-outline" onClick={onClear}>
							<i className="bi bi-x-lg"></i> Hủy bỏ
						</Button>
					</div>
				</Col>
			</Row>
		</div>
	);
};

export default DrugInfo;
